import { useState, useRef, type FormEvent } from 'react';
import { useTenantStore } from '@/store/tenantStore';
import type { LoginState } from '@/hooks/useLogin';
import { useLocalization } from '@/hooks/useLocalization';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { translateDynamicContent } from '@/i18n';
import { validateEmail, validatePassword } from '@/utils/formValidators';
import styles from '../AuthPage.module.css';

/**
 * SignInCard — email/password sign in.
 *
 * Mirrors Flutter WebLoginForms (email tab) + WebLoginBloc._OnLoginWithEmail, with the
 * WebLoginPageConstants texts and FormValidators.validateEmail / validatePassword.
 * Google / Apple / phone login are commented out in Flutter web, so they are not offered.
 */

interface SignInCardProps {
  login: LoginState;
  onForgotPassword: (email: string) => void;
}

export function SignInCard({ login, onForgotPassword }: SignInCardProps) {
  const { preferences } = useTenantStore();
  const { t } = useLocalization();
  const isMobile = useIsMobile();
  const { isLoading, error, handleEmailLogin, clearError } = login;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const passwordRef = useRef<HTMLInputElement>(null);

  // Flutter (desktop only): localizationDynamicContent.getValue(tenantPreferences['loginFormWelcomeText'])
  const loginFormWelcomeText = (preferences as Record<string, unknown> | null)?.['loginFormWelcomeText'] as
    | string
    | undefined;
  const loginWelcomeText = loginFormWelcomeText != null && !isMobile
    ? translateDynamicContent(loginFormWelcomeText)
    : undefined;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    clearError();

    // Flutter validates the trimmed values; the bloc then lower-cases the email
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();

    const eErr = validateEmail(trimmedEmail, t);
    const pErr = validatePassword(trimmedPassword, t);
    setEmailError(eErr);
    setPasswordError(pErr);

    if (eErr || pErr) return;
    handleEmailLogin(trimmedEmail.toLowerCase(), trimmedPassword);
  }

  return (
    <section className={styles.card} aria-labelledby="sign-in-title">
      {loginWelcomeText && <p className={styles.welcome}>{loginWelcomeText}</p>}
      <h1 id="sign-in-title" className={styles.title}>{t('Sign In')}</h1>

      {error && (
        <div className={styles.errorBanner} role="alert">{t(error)}</div>
      )}

      <form className={styles.form} onSubmit={handleSubmit} autoComplete="on" noValidate>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="sign-in-email">
            {t('Email ID')} <span className={styles.required}>*</span>
          </label>
          <input
            id="sign-in-email"
            type="email"
            name="email"
            autoComplete="username"
            placeholder={t('Enter your Email Id')}
            className={`${styles.input} ${emailError ? styles.inputError : ''}`}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError('');
              if (error) clearError();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                passwordRef.current?.focus();
              }
            }}
            disabled={isLoading}
          />
          {emailError && <span className={styles.fieldError}>{emailError}</span>}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="sign-in-password">
            {t('Password')} <span className={styles.required}>*</span>
          </label>
          <div className={styles.passwordWrapper}>
            <input
              id="sign-in-password"
              ref={passwordRef}
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="current-password"
              placeholder={t('Enter your Password')}
              className={`${styles.input} ${passwordError ? styles.inputError : ''}`}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError('');
                if (error) clearError();
              }}
              disabled={isLoading}
            />
            <button
              type="button"
              className={styles.eyeBtn}
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              {showPassword ? '👁' : '👁‍🗨'}
            </button>
          </div>
          {passwordError && <span className={styles.fieldError}>{passwordError}</span>}
        </div>

        <div className={styles.forgot}>
          <button
            type="button"
            className={styles.linkBtn}
            onClick={() => onForgotPassword(email.trim())}
          >
            {t('Forgot Password ?')}
          </button>
        </div>

        <button type="submit" className={styles.primaryBtn} disabled={isLoading}>
          {t('Login')}
        </button>
      </form>
    </section>
  );
}
